import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import UpgradeModal from "@/components/UpgradeModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

export default function Finances() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [incomes, setIncomes] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [incomeFormData, setIncomeFormData] = useState({
    description: "",
    amount: "",
    date: "",
    project_id: "",
    customer_id: "",
    invoice_number: "",
    vat_rate: "19",
  });
  const [expenseFormData, setExpenseFormData] = useState({
    description: "",
    amount: "",
    date: "",
    category: "office",
    vat_rate: "19",
    is_deductible: true,
  });

  useEffect(() => {
    fetchIncomes();
    fetchExpenses();
    fetchProjects();
    fetchCustomers();
  }, []);

  const fetchIncomes = async () => {
    try {
      const response = await api.get('/incomes');
      const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setIncomes(sorted);
    } catch (error) {
      console.error("Error fetching incomes:", error);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/expenses');
      const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setExpenses(sorted);
    } catch (error) {
      console.error("Error fetching expenses:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...incomeFormData,
        amount: parseFloat(incomeFormData.amount),
        vat_rate: parseFloat(incomeFormData.vat_rate),
        date: new Date(incomeFormData.date).toISOString(),
        project_id: incomeFormData.project_id || undefined,
        customer_id: incomeFormData.customer_id || undefined,
        invoice_number: incomeFormData.invoice_number || undefined,
      };

      if (editingIncome) {
        await api.put(`/incomes/${editingIncome.id}`, data);
        toast.success("Einnahme aktualisiert");
      } else {
        await api.post(`/incomes`, data);
        toast.success("Einnahme erstellt");
      }

      setIncomeDialogOpen(false);
      setEditingIncome(null);
      setIncomeFormData({
        description: "",
        amount: "",
        date: "",
        project_id: "",
        customer_id: "",
        invoice_number: "",
        vat_rate: "19",
      });
      fetchIncomes();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Fehler beim Speichern");
      }
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...expenseFormData,
        amount: parseFloat(expenseFormData.amount),
        vat_rate: parseFloat(expenseFormData.vat_rate),
        date: new Date(expenseFormData.date).toISOString(),
      };

      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, data);
        toast.success("Ausgabe aktualisiert");
      } else {
        await api.post(`/expenses`, data);
        toast.success("Ausgabe erstellt");
      }

      setExpenseDialogOpen(false);
      setEditingExpense(null);
      setExpenseFormData({
        description: "",
        amount: "",
        date: "",
        category: "office",
        vat_rate: "19",
        is_deductible: true,
      });
      fetchExpenses();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Fehler beim Speichern");
      }
    }
  };

  const deleteIncome = async (id) => {
    if (!window.confirm("Möchten Sie diese Einnahme wirklich löschen?")) return;
    try {
      await api.delete(`/incomes/${id}`);
      toast.success("Einnahme gelöscht");
      fetchIncomes();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Möchten Sie diese Ausgabe wirklich löschen?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Ausgabe gelöscht");
      fetchExpenses();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const totalIncomes = incomes.reduce((sum, i) => sum + i.amount, 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const netProfit = totalIncomes - totalExpenses;

  return (
    <div className="space-y-8" data-testid="finances-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Finanzen</h1>
        <p className="text-lg text-gray-600">Einnahmen und Ausgaben verwalten</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Gesamt Einnahmen</CardTitle>
            <div className="p-2 rounded-lg bg-green-50">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600" data-testid="total-incomes">
              {totalIncomes.toFixed(2)} €
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Gesamt Ausgaben</CardTitle>
            <div className="p-2 rounded-lg bg-red-50">
              <TrendingDown className="h-5 w-5 text-red-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600" data-testid="total-expenses">
              {totalExpenses.toFixed(2)} €
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Nettogewinn</CardTitle>
            <div className="p-2 rounded-lg bg-emerald-50">
              <DollarSign className="h-5 w-5 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600" data-testid="net-profit">
              {netProfit.toFixed(2)} €
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Incomes and Expenses */}
      <Tabs defaultValue="incomes" className="space-y-6">
        <TabsList>
          <TabsTrigger value="incomes" data-testid="incomes-tab">Einnahmen</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="expenses-tab">Ausgaben</TabsTrigger>
        </TabsList>

        {/* Incomes Tab */}
        <TabsContent value="incomes" className="space-y-6">
          <div className="flex justify-end">
            <Dialog open={incomeDialogOpen} onOpenChange={setIncomeDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-income-button" onClick={() => {
                  setEditingIncome(null);
                  setIncomeFormData({
                    description: "",
                    amount: "",
                    date: "",
                    project_id: "",
                    customer_id: "",
                    invoice_number: "",
                    vat_rate: "19",
                  });
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Neue Einnahme
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingIncome ? t('finances.editIncome') : t('finances.newIncome')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleIncomeSubmit} className="space-y-4">
                  <div>
                    <Label>Beschreibung *</Label>
                    <Input
                      data-testid="income-description-input"
                      value={incomeFormData.description}
                      onChange={(e) => setIncomeFormData({ ...incomeFormData, description: e.target.value })}
                      placeholder="z.B. Rechnung #001"
                      required
                    />
                  </div>
                  <div>
                    <Label>Betrag (€) *</Label>
                    <Input
                      data-testid="income-amount-input"
                      type="number"
                      step="0.01"
                      value={incomeFormData.amount}
                      onChange={(e) => setIncomeFormData({ ...incomeFormData, amount: e.target.value })}
                      placeholder="1500.00"
                      required
                    />
                  </div>
                  <div>
                    <Label>Datum *</Label>
                    <Input
                      data-testid="income-date-input"
                      type="date"
                      value={incomeFormData.date}
                      onChange={(e) => setIncomeFormData({ ...incomeFormData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Projekt</Label>
                    <Select
                      value={incomeFormData.project_id}
                      onValueChange={(value) => setIncomeFormData({ ...incomeFormData, project_id: value })}
                    >
                      <SelectTrigger data-testid="income-project-select">
                        <SelectValue placeholder="Projekt wählen (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rechnungsnummer</Label>
                    <Input
                      data-testid="invoice-number-input"
                      value={incomeFormData.invoice_number}
                      onChange={(e) => setIncomeFormData({ ...incomeFormData, invoice_number: e.target.value })}
                      placeholder="RE-2024-001"
                    />
                  </div>
                  <div>
                    <Label>USt-Satz (%)</Label>
                    <Select
                      value={incomeFormData.vat_rate}
                      onValueChange={(value) => setIncomeFormData({ ...incomeFormData, vat_rate: value })}
                    >
                      <SelectTrigger data-testid="income-vat-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% (Steuerfrei)</SelectItem>
                        <SelectItem value="7">7% (Ermäßigt)</SelectItem>
                        <SelectItem value="19">19% (Normal)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="save-income-button">
                    {editingIncome ? t('common.update') : t('common.create')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Datum</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Beschreibung</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Projekt/Kunde</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Rechnung</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Betrag</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomes.map((income) => (
                      <tr key={income.id} className="border-b hover:bg-gray-50" data-testid={`income-row-${income.id}`}>
                        <td className="py-3 px-4">
                          {format(new Date(income.date), "dd.MM.yyyy", { locale: de })}
                        </td>
                        <td className="py-3 px-4">{income.description}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {income.project_name && <div>{income.project_name}</div>}
                          {income.customer_name && <div className="text-xs">{income.customer_name}</div>}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{income.invoice_number || "-"}</td>
                        <td className="py-3 px-4 text-right font-semibold text-green-600">
                          {income.amount.toFixed(2)} €
                        </td>
                        <td className="py-3 px-4 text-right space-x-2">
                          <Button
                            data-testid={`edit-income-${income.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingIncome(income);
                              setIncomeFormData({
                                description: income.description,
                                amount: income.amount.toString(),
                                date: format(new Date(income.date), "yyyy-MM-dd"),
                                project_id: income.project_id || "",
                                customer_id: income.customer_id || "",
                                invoice_number: income.invoice_number || "",
                                vat_rate: income.vat_rate.toString(),
                              });
                              setIncomeDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-income-${income.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteIncome(income.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {incomes.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    Keine Einnahmen vorhanden
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-6">
          <div className="flex justify-end">
            <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-expense-button" onClick={() => {
                  setEditingExpense(null);
                  setExpenseFormData({
                    description: "",
                    amount: "",
                    date: "",
                    category: "office",
                    vat_rate: "19",
                    is_deductible: true,
                  });
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Neue Ausgabe
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingExpense ? t('finances.editExpense') : t('finances.newExpense')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleExpenseSubmit} className="space-y-4">
                  <div>
                    <Label>Beschreibung *</Label>
                    <Input
                      data-testid="expense-description-input"
                      value={expenseFormData.description}
                      onChange={(e) => setExpenseFormData({ ...expenseFormData, description: e.target.value })}
                      placeholder="z.B. Büromaterial"
                      required
                    />
                  </div>
                  <div>
                    <Label>Betrag (€) *</Label>
                    <Input
                      data-testid="expense-amount-input"
                      type="number"
                      step="0.01"
                      value={expenseFormData.amount}
                      onChange={(e) => setExpenseFormData({ ...expenseFormData, amount: e.target.value })}
                      placeholder="250.00"
                      required
                    />
                  </div>
                  <div>
                    <Label>Datum *</Label>
                    <Input
                      data-testid="expense-date-input"
                      type="date"
                      value={expenseFormData.date}
                      onChange={(e) => setExpenseFormData({ ...expenseFormData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Kategorie</Label>
                    <Select
                      value={expenseFormData.category}
                      onValueChange={(value) => setExpenseFormData({ ...expenseFormData, category: value })}
                    >
                      <SelectTrigger data-testid="expense-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="office">Büro</SelectItem>
                        <SelectItem value="travel">Reise</SelectItem>
                        <SelectItem value="equipment">Ausrüstung</SelectItem>
                        <SelectItem value="software">Software</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="other">Sonstiges</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>USt-Satz (%)</Label>
                    <Select
                      value={expenseFormData.vat_rate}
                      onValueChange={(value) => setExpenseFormData({ ...expenseFormData, vat_rate: value })}
                    >
                      <SelectTrigger data-testid="expense-vat-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% (Steuerfrei)</SelectItem>
                        <SelectItem value="7">7% (Ermäßigt)</SelectItem>
                        <SelectItem value="19">19% (Normal)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="save-expense-button">
                    {editingExpense ? t('common.update') : t('common.create')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Datum</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Beschreibung</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Kategorie</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Betrag</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((expense) => (
                      <tr key={expense.id} className="border-b hover:bg-gray-50" data-testid={`expense-row-${expense.id}`}>
                        <td className="py-3 px-4">
                          {format(new Date(expense.date), "dd.MM.yyyy", { locale: de })}
                        </td>
                        <td className="py-3 px-4">{expense.description}</td>
                        <td className="py-3 px-4 text-sm text-gray-600 capitalize">{expense.category}</td>
                        <td className="py-3 px-4 text-right font-semibold text-red-600">
                          {expense.amount.toFixed(2)} €
                        </td>
                        <td className="py-3 px-4 text-right space-x-2">
                          <Button
                            data-testid={`edit-expense-${expense.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingExpense(expense);
                              setExpenseFormData({
                                description: expense.description,
                                amount: expense.amount.toString(),
                                date: format(new Date(expense.date), "yyyy-MM-dd"),
                                category: expense.category,
                                vat_rate: expense.vat_rate.toString(),
                                is_deductible: expense.is_deductible,
                              });
                              setExpenseDialogOpen(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-expense-${expense.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteExpense(expense.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {expenses.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    Keine Ausgaben vorhanden
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      

    <UpgradeModal

      isOpen={upgradeModalOpen}

      onClose={() => setUpgradeModalOpen(false)}

      message={upgradeMessage}

      currentPlan={user?.plan}

    />

    </div>
  );
}